import {
  Box,
  Button,
  Card,
  Checkbox,
  Grid,
  Group,
  NumberInput,
  SegmentedControl,
  Table,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core';
import {
  IconAdjustments,
  IconAlertCircle,
  IconBrandAndroid,
  IconBrandApple,
  IconBrandChrome,
  IconChevronLeft,
  IconChevronRight,
  IconCopy,
  IconDeviceFloppy,
  IconDeviceMobile,
  IconInbox,
  IconMail,
  IconPlus,
  IconQuestionMark,
  IconSend,
  IconSettings,
  IconTrash,
} from '@tabler/icons-react';
import queryString from 'query-string';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import getMergeTags from '../../constants/getMergeTags';
import useFetch from '../../helpers/useFetch';
import { validateHandlebarsValue } from '../../helpers/validateHandlebars';
import DailyLimitSettings from '../DailyLimitSettings';
import ErrorMessage from '../ErrorMessage';
import AppInboxEditor from '../InboxMessages/AppInboxEditor';
import LockableButton from '../Lockable/LockableButton';
import ConfirmationModal from '../Modals/ConfirmationModal';
import SendTestMessageModal from '../Modals/SendTestMessageModal';
import ProductFeedSelector from '../ProductFeeds/ProductFeedSelector';
import Spinner from '../Spinner';
import WarningMessageOnDelete from '../WarningMessageOnDelete';
import PushNotificationContent from './PushNotificationContent';
import styles from './PushNotificationForm.module.css';

const Mapper = ({ rows, setRows }) => {
  const setRow = (i, row) => {
    const rowsCopy = structuredClone(rows);
    rowsCopy[i] = row;
    setRows(rowsCopy);
  };

  const deleteRow = (i) => setRows(structuredClone(rows).filter((_, index) => i !== index));

  return (
    <>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Key</Table.Th>
            <Table.Th>Value</Table.Th>
            <Table.Th ta="right">
              {' '}
              <Button size="xs" variant="default" onClick={() => setRows([...rows, { key: '', value: '' }])}>
                <IconPlus size={14} /> Add Variable
              </Button>
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row, i) => {
            return (
              <Table.Tr key={i}>
                <Table.Td>
                  <TextInput
                    name="key"
                    size="xs"
                    value={row.key}
                    placeholder="The property you wish to send"
                    onChange={(e) => setRow(i, { ...row, key: e.target.value })}
                  />
                </Table.Td>
                <Table.Td>
                  <Textarea
                    name="value"
                    size="xs"
                    placeholder="The value you wish to send for this property"
                    rows={1}
                    value={row.value}
                    onChange={(e) => setRow(i, { ...row, value: e.target.value })}
                  />
                </Table.Td>
                <Table.Td ta="right">
                  <Button size="xs" variant="default" onClick={() => deleteRow(i)}>
                    <IconTrash size={14} />
                  </Button>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </>
  );
};

const PushNotificationForm = ({
  pushNotification: passedPushNotification,
  deletePushNotification,
  savePushNotification,
  showUserFlow,
  deleteError,
  onBack,
}) => {
  const location = useLocation();
  const fetch = useFetch();
  const navigate = useNavigate();
  const search = queryString.parse(location.search);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [productFeeds, setProductFeeds] = useState();
  const [customFields, setCustomFields] = useState({});
  const [firebaseAccounts, setFirebaseAccounts] = useState(null);
  const [pushNotification, setPushNotification] = useState(passedPushNotification);
  const [error, setError] = useState();
  const [coupons, setCoupons] = useState();
  const [surveys, setSurveys] = useState([]);

  // App Inbox state
  const existingInbox = passedPushNotification?.inboxMessage;
  const [appInbox, setAppInbox] = useState(
    existingInbox
      ? {
          title: existingInbox.title,
          html: existingInbox.html,
          design: existingInbox.design,
          ttlSeconds: existingInbox.ttlSeconds,
        }
      : null
  );
  const appInboxEditorRef = useRef(null);
  const [testInboxModalOpen, setTestInboxModalOpen] = useState(false);
  const [testInboxSending, setTestInboxSending] = useState(false);
  const [testInboxError, setTestInboxError] = useState();

  const { accountId, domainId } = useParams();
  const { domain } = useSelector((state) => state.domain);
  const tabs = ['general', 'webContent', 'androidContent', 'iosContent', 'appInboxContent', 'behavior'].filter((t) => {
    if (t === 'appInboxContent') return pushNotification.platforms.includes('appInbox');
    if (t === 'behavior') return true;
    return !t.endsWith('Content') || pushNotification.platforms.includes(t.replace('Content', ''));
  });
  const tab = tabs.includes(search.pushNotificationFormTab) ? search.pushNotificationFormTab : tabs[0];

  const pushPlatforms = pushNotification.platforms.filter((p) => p !== 'appInbox');

  const MAX_URL_LENGTH = 2000;

  const isValidUrl = (url) => {
    if (!url) return false;
    if (/\{\{.+?\}\}/.test(url)) return true;
    try {
      const parsed = new URL(url);
      return ['http:', 'https:'].includes(parsed.protocol) || parsed.protocol.endsWith(':');
    } catch {
      return false;
    }
  };

  const webUrlError = pushNotification.url
    ? pushNotification.url.length > MAX_URL_LENGTH
      ? `URL must be ${MAX_URL_LENGTH} characters or fewer.`
      : !isValidUrl(pushNotification.url)
        ? 'Please enter a valid URL.'
        : null
    : null;
  const mobileUrlError = pushNotification.mobileTargetUrl
    ? pushNotification.mobileTargetUrl.length > MAX_URL_LENGTH
      ? `URL must be ${MAX_URL_LENGTH} characters or fewer.`
      : !isValidUrl(pushNotification.mobileTargetUrl)
        ? 'Please enter a valid URL.'
        : null
    : null;

  const platformHandlebarsErrors = Object.fromEntries(
    pushPlatforms.map((platform) => [
      platform,
      {
        subject: validateHandlebarsValue(pushNotification.content[platform]?.subject),
        body: validateHandlebarsValue(pushNotification.content[platform]?.body),
      },
    ])
  );
  const appInboxTitleHandlebarsError = pushNotification.platforms.includes('appInbox')
    ? validateHandlebarsValue(appInbox?.title)
    : null;
  const hasHandlebarsErrors =
    pushPlatforms.some((platform) => platformHandlebarsErrors[platform].subject || platformHandlebarsErrors[platform].body) ||
    Boolean(appInboxTitleHandlebarsError);

  const isValid =
    pushPlatforms.every(
      (platform) => pushNotification.content[platform]?.subject && pushNotification.content[platform]?.body
    ) &&
    pushNotification.name &&
    (!pushPlatforms.length || (pushNotification.tag && pushNotification.ttl)) &&
    (!pushPlatforms.includes('web') || (pushNotification.url && !webUrlError)) &&
    (pushNotification.type !== 'recommender' || pushNotification.productFeedId) &&
    pushNotification.platforms.length > 0 &&
    (!pushNotification.platforms.includes('appInbox') || (appInbox && appInbox.title)) &&
    (pushNotification.mobileTarget !== 'url' || (pushNotification.mobileTargetUrl && !mobileUrlError)) &&
    (pushNotification.mobileTarget !== 'screen' || pushNotification.mobileTargetScreen) &&
    !hasHandlebarsErrors;

  // Determine which mobile platforms have Firebase credentials configured.
  // - unified appType: 'both' credential covers android + ios
  // - separate appType: individual 'android' and 'ios' credentials
  const hasAndroidFirebase =
    firebaseAccounts != null &&
    (firebaseAccounts.some((a) => a.platform === 'both') || firebaseAccounts.some((a) => a.platform === 'android'));
  const hasIosFirebase =
    firebaseAccounts != null &&
    (firebaseAccounts.some((a) => a.platform === 'both') || firebaseAccounts.some((a) => a.platform === 'ios'));
  const hasAnyMobileFirebase = hasAndroidFirebase || hasIosFirebase;
  const firebaseSettingsUrl = `#/accounts/${accountId}/domains/${domainId}/settings/messagingAndAds?tab=pushNotification`;

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/productFeeds`)
      .then(({ productFeeds }) => {
        setProductFeeds(productFeeds.filter((f) => f.scope === 'marketingChannels'));
      })
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/customFields`)
      .then(({ customFields }) => setCustomFields(customFields))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/coupons`)
      .then(({ coupons }) => setCoupons(coupons))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/surveys`)
      .then(({ surveys }) => setSurveys(surveys))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/firebaseAccounts`)
      .then(({ firebaseAccounts }) => setFirebaseAccounts(firebaseAccounts || []))
      .catch(setError);
  }, [fetch, accountId, domainId]);

  if (pushNotification === undefined || !customFields || !productFeeds) {
    return <Spinner />;
  }

  if (pushNotification === null) {
    return <ErrorMessage message="Push notification does not exist!" />;
  }

  const handleChange = (key, value) => {
    setPushNotification({ ...pushNotification, [key]: value });
  };

  const mergeTags = customFields
    ? getMergeTags(customFields).filter(
        (el) =>
          ![
            'profileId',
            'unsubscribeLink',
            'subscribeLink',
            'viewInBrowserLink',
            'subscriptionManagementLink',
            'dynamicCoupons',
          ].includes(el.key) && !el.key.startsWith('surveyLink')
      )
    : [];

  const handleSave = () => {
    const appInboxPayload = pushNotification.platforms.includes('appInbox') ? appInbox : null;

    if (appInboxPayload && appInboxEditorRef.current) {
      appInboxEditorRef.current.exportDesign((exported) => {
        savePushNotification({ ...pushNotification, appInbox: exported });
      });
    } else {
      savePushNotification({ ...pushNotification, appInbox: appInboxPayload });
    }
  };

  const sendTestInboxMessage = (sendTo, impersonateProfileQuery, triggerEventAction, cb) => {
    setTestInboxError(undefined);
    setTestInboxSending(true);
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/pushNotifications/testInboxMessage`, {
      method: 'POST',
      body: JSON.stringify({
        pushNotificationId: pushNotification.id,
        sendTo,
        impersonateProfileQuery,
        triggerEventAction,
      }),
    })
      .then(() => cb())
      .catch(setTestInboxError)
      .finally(() => setTestInboxSending(false));
  };

  let deleteButton;
  if (deletePushNotification) {
    deleteButton = (
      <LockableButton restrictionKey="pushNotificationsDelete" color="red" onClick={() => setConfirmDeleteOpen(true)}>
        <IconTrash size={14} /> Delete
      </LockableButton>
    );
  }

  let duplicateButton;
  if (pushNotification.id) {
    duplicateButton = (
      <LockableButton
        restrictionKey="pushNotificationsCreate"
        color="cyan"
        onClick={() => {
          navigate(
            location.pathname.replace(`/push/${pushNotification.id}/details`, `/push/new?from=${pushNotification.id}`)
          );
        }}
      >
        <IconCopy size={14} /> Make a copy
      </LockableButton>
    );
  }

  const setTab = (pushNotificationFormTab) => {
    navigate(`${location.pathname}?${queryString.stringify({ ...search, pushNotificationFormTab })}`);
  };

  const formActionButtons = (
    <>
      <Button variant="default" onClick={onBack}>
        <IconChevronLeft /> Back
      </Button>
      {deleteButton}
      {duplicateButton}
      {showUserFlow && (
        <Button variant="default" onClick={() => showUserFlow()}>
          <IconMail size={14} /> Send
        </Button>
      )}
      {pushNotification.id || tabs.indexOf(tab) === tabs.length - 1 ? (
        <LockableButton
          restrictionKey={`pushNotifications${pushNotification.id ? 'Update' : 'Create'}`}
          variant="primary"
          onClick={handleSave}
          disabled={!isValid}
        >
          <IconDeviceFloppy size={14} /> Save
        </LockableButton>
      ) : (
        <Button
          onClick={() => {
            setTab(tabs[tabs.indexOf(tab) + 1]);
            window.scrollTo(0, 0);
          }}
        >
          Next <IconChevronRight size={14} />
        </Button>
      )}
    </>
  );

  return (
    <Grid.Col span={12}>
      <Tabs value={tab} onChange={setTab} mb="md" keepMounted>
        <Tabs.List>
          <Tabs.Tab value="general">
            <IconSettings size={14} /> General
          </Tabs.Tab>
          {pushNotification.platforms.includes('web') && (
            <Tabs.Tab
              value="webContent"
              rightSection={
                platformHandlebarsErrors.web?.subject || platformHandlebarsErrors.web?.body ? (
                  <IconAlertCircle size={14} color="var(--mantine-color-red-6)" />
                ) : null
              }
            >
              <IconBrandChrome size={14} /> Web
            </Tabs.Tab>
          )}
          {pushNotification.platforms.includes('android') && (
            <Tabs.Tab
              value="androidContent"
              rightSection={
                platformHandlebarsErrors.android?.subject || platformHandlebarsErrors.android?.body ? (
                  <IconAlertCircle size={14} color="var(--mantine-color-red-6)" />
                ) : null
              }
            >
              <IconBrandAndroid size={14} /> Android
            </Tabs.Tab>
          )}
          {pushNotification.platforms.includes('ios') && (
            <Tabs.Tab
              value="iosContent"
              rightSection={
                platformHandlebarsErrors.ios?.subject || platformHandlebarsErrors.ios?.body ? (
                  <IconAlertCircle size={14} color="var(--mantine-color-red-6)" />
                ) : null
              }
            >
              <IconBrandApple size={14} /> IOS
            </Tabs.Tab>
          )}
          {pushNotification.platforms.includes('appInbox') && (
            <Tabs.Tab
              value="appInboxContent"
              rightSection={
                appInboxTitleHandlebarsError ? (
                  <IconAlertCircle size={14} color="var(--mantine-color-red-6)" />
                ) : null
              }
            >
              <IconInbox size={14} /> App Inbox
            </Tabs.Tab>
          )}
          <Tabs.Tab value="behavior">
            <IconAdjustments size={14} /> Behavior
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="general" pt="md">
          <Card withBorder>
            <Card.Section inheritPadding py="md">
              <Grid mb="md">
                <Grid.Col span={{ base: 12, sm: 2 }}>
                  <Text fw={500}>
                    Name
                    <Text span c="red">
                      *
                    </Text>
                  </Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 8 }}>
                  <TextInput
                    name="name"
                    value={pushNotification.name}
                    placeholder="e.g. Check This Out!"
                    onChange={(e) => handleChange('name', e.target.value)}
                  />
                </Grid.Col>
              </Grid>
              <Grid>
                <Grid.Col span={{ base: 12, sm: 2 }} my="lg">
                  <Text fw={500}>
                    Channels
                    <Text span c="red">
                      *
                    </Text>
                  </Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 8 }} my="lg">
                  <Checkbox
                    label="Web"
                    onChange={(event) => {
                      const checked = event.target.checked;
                      const updated = {
                        ...pushNotification,
                        platforms: checked
                          ? [...pushNotification.platforms, 'web']
                          : pushNotification.platforms.filter((c) => c !== 'web'),
                      };
                      if (checked && !pushNotification.content?.web) {
                        updated.content = { ...pushNotification.content, web: { subject: '', body: '' } };
                      }
                      setPushNotification(updated);
                    }}
                    checked={pushNotification.platforms.includes('web')}
                    mb="xs"
                  />
                  <Checkbox
                    label="Android"
                    disabled={!hasAndroidFirebase}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      const updated = {
                        ...pushNotification,
                        platforms: checked
                          ? [...pushNotification.platforms, 'android']
                          : pushNotification.platforms.filter((c) => c !== 'android'),
                      };
                      if (checked && !pushNotification.content?.android) {
                        updated.content = { ...pushNotification.content, android: { subject: '', body: '' } };
                      }
                      setPushNotification(updated);
                    }}
                    checked={pushNotification.platforms.includes('android')}
                    mb="xs"
                  />
                  <Checkbox
                    label="IOS"
                    disabled={!hasIosFirebase}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      const updated = {
                        ...pushNotification,
                        platforms: checked
                          ? [...pushNotification.platforms, 'ios']
                          : pushNotification.platforms.filter((c) => c !== 'ios'),
                      };
                      if (checked && !pushNotification.content?.ios) {
                        updated.content = { ...pushNotification.content, ios: { subject: '', body: '' } };
                      }
                      setPushNotification(updated);
                    }}
                    checked={pushNotification.platforms.includes('ios')}
                    mb="xs"
                  />
                  <Checkbox
                    label="App Inbox"
                    disabled={!hasAnyMobileFirebase}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      const updated = {
                        ...pushNotification,
                        platforms: checked
                          ? [...pushNotification.platforms, 'appInbox']
                          : pushNotification.platforms.filter((c) => c !== 'appInbox'),
                      };
                      // Reset reschedule to send when appInbox is enabled (reschedule is invalid with appInbox)
                      if (checked && pushNotification.onDailyLimitReached === 'reschedule') {
                        updated.onDailyLimitReached = 'send';
                      }
                      // Reset inbox mobile target when appInbox is unchecked
                      if (!checked && pushNotification.mobileTarget === 'inbox') {
                        updated.mobileTarget = null;
                      }
                      setPushNotification(updated);
                      if (checked && !appInbox) {
                        setAppInbox({ title: null, html: '', design: null, ttlSeconds: null });
                      }
                    }}
                    checked={pushNotification.platforms.includes('appInbox')}
                  />
                  {!hasAnyMobileFirebase && firebaseAccounts != null && (
                    <Text size="xs" c="orange" mt="xs">
                      Android, iOS, and App Inbox require Firebase credentials.{' '}
                      <a href={firebaseSettingsUrl} target="_blank" rel="noreferrer">
                        Add a Firebase service account in Settings &rarr; Messaging &rarr; Push Notifications
                      </a>
                      .
                    </Text>
                  )}
                  {hasAnyMobileFirebase && (!hasAndroidFirebase || !hasIosFirebase) && (
                    <Text size="xs" c="orange" mt="xs">
                      {!hasAndroidFirebase ? 'Android' : 'iOS'} is disabled — no Firebase credentials found for this
                      platform.{' '}
                      <a href={firebaseSettingsUrl} target="_blank" rel="noreferrer">
                        Add credentials in Settings &rarr; Messaging &rarr; Push Notifications
                      </a>
                      .
                    </Text>
                  )}
                </Grid.Col>
              </Grid>
              {pushNotification.platforms.some((p) => ['web', 'android', 'ios'].includes(p)) && (
                <>
                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 2 }} my="lg">
                      <Text fw={500}>Type*</Text>
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 8 }} my="lg">
                      <SegmentedControl
                        disabled={!!pushNotification.id}
                        value={pushNotification.type ?? ''}
                        onChange={(type) => {
                          const change = {};
                          if (type === 'recommender') {
                            change.iconUrl = '{{product.imageUrl}}';
                            if (!pushNotification.btnText) {
                              change.btnText = 'Shop Now';
                            }
                            change.url = '{{product.url}}';
                            if (!pushNotification.body) {
                              change.body = `Hi {{firstName}},

Only for a limited time!

{{product.name}} - {{formatPrice product "price"}}`;
                            }
                          }
                          setPushNotification({ ...pushNotification, ...change, type });
                        }}
                        data={[
                          { label: 'Message', value: 'message' },
                          { label: 'Recommender', value: 'recommender' },
                        ]}
                      />
                    </Grid.Col>
                  </Grid>
                  {pushNotification.type === 'recommender' && (
                    <Grid mb="md">
                      <Grid.Col span={{ base: 12, sm: 2 }}>
                        <Text fw={500}>Product Feed*</Text>
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, sm: 10 }}>
                        <ProductFeedSelector
                          accountId={accountId}
                          domainId={domainId}
                          productFeedId={pushNotification.productFeedId}
                          onSelect={(productFeedId) => setPushNotification({ ...pushNotification, productFeedId })}
                          forType="push notification"
                          scope="marketingChannels"
                        />
                      </Grid.Col>
                    </Grid>
                  )}
                </>
              )}
            </Card.Section>
          </Card>
          <Group justify="flex-end" gap="xs" pt="md">
            {formActionButtons}
          </Group>
        </Tabs.Panel>

        {pushNotification.platforms.includes('web') && (
          <Tabs.Panel value="webContent" pt="md">
            <PushNotificationContent
              pushNotification={pushNotification}
              setPushNotification={setPushNotification}
              platform="web"
              coupons={coupons}
              surveys={surveys}
              customFields={customFields}
              actionButtons={formActionButtons}
              subjectHandlebarsError={platformHandlebarsErrors.web?.subject}
              bodyHandlebarsError={platformHandlebarsErrors.web?.body}
            />
          </Tabs.Panel>
        )}

        {pushNotification.platforms.includes('android') && (
          <Tabs.Panel value="androidContent" pt="md">
            <PushNotificationContent
              pushNotification={pushNotification}
              setPushNotification={setPushNotification}
              platform="android"
              coupons={coupons}
              surveys={surveys}
              customFields={customFields}
              actionButtons={formActionButtons}
              subjectHandlebarsError={platformHandlebarsErrors.android?.subject}
              bodyHandlebarsError={platformHandlebarsErrors.android?.body}
            />
          </Tabs.Panel>
        )}

        {pushNotification.platforms.includes('ios') && (
          <Tabs.Panel value="iosContent" pt="md">
            <PushNotificationContent
              pushNotification={pushNotification}
              setPushNotification={setPushNotification}
              platform="ios"
              coupons={coupons}
              surveys={surveys}
              customFields={customFields}
              actionButtons={formActionButtons}
              subjectHandlebarsError={platformHandlebarsErrors.ios?.subject}
              bodyHandlebarsError={platformHandlebarsErrors.ios?.body}
            />
          </Tabs.Panel>
        )}

        {pushNotification.platforms.includes('appInbox') && appInbox && (
          <Tabs.Panel value="appInboxContent" pt="md">
            <AppInboxEditor
              ref={appInboxEditorRef}
              inboxData={appInbox}
              onChange={setAppInbox}
              mergeTags={mergeTags}
              extraActions={
                pushNotification.id && (
                  <Button size="sm" variant="default" onClick={() => setTestInboxModalOpen(true)}>
                    <IconSend size={13} style={{ marginRight: 4 }} /> Test
                  </Button>
                )
              }
            />
            <SendTestMessageModal
              show={testInboxModalOpen}
              close={() => setTestInboxModalOpen(false)}
              send={sendTestInboxMessage}
              sending={testInboxSending}
              error={testInboxError}
              sendToPlaceholder="email, phone or profile id"
              sendToHelperText="The test inbox message will be delivered to this profile's in-app inbox."
            />
            <Group justify="flex-end" gap="xs" pt="md">
              {formActionButtons}
            </Group>
          </Tabs.Panel>
        )}

        <Tabs.Panel value="behavior" pt="md">
          {pushNotification.platforms.some((p) => ['web', 'android', 'ios'].includes(p)) && (
            <Card withBorder mb="md">
              <Card.Section inheritPadding py="md">
                <Text fw={700} size="lg" mb="md">
                  <IconSend size={16} /> Push Delivery Options
                </Text>
                <Grid mb="md">
                  <Grid.Col span={{ base: 12, sm: 2 }}>
                    <Text fw={500}>Tag</Text>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 10 }}>
                    <TextInput
                      name="tag"
                      value={pushNotification.tag}
                      error={
                        !pushNotification.tag ? 'You may use any string as tag, but this field can not be empty.' : null
                      }
                      placeholder="New Year Campaign!"
                      onChange={(e) => handleChange('tag', e.target.value)}
                    />
                    <Text size="sm" c="dimmed" mt="xs">
                      The tag option is essentially a string ID that groups notifications together, providing an easy
                      way to determine how multiple notifications are displayed to the user.
                    </Text>
                  </Grid.Col>
                </Grid>
                <Grid mb="md">
                  <Grid.Col span={{ base: 12, sm: 2 }}>
                    <Text fw={500}>Message Expire Time</Text>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 10 }}>
                    <NumberInput
                      name="ttl"
                      value={pushNotification.ttl || ''}
                      error={!pushNotification.ttl ? 'This field can not be empty.' : null}
                      placeholder="168"
                      min={1}
                      allowDecimal={false}
                      allowNegative={false}
                      onChange={(value) => handleChange('ttl', value)}
                      rightSection={
                        <Text size="sm" c="dimmed">
                          hours
                        </Text>
                      }
                      rightSectionWidth={50}
                    />
                    <Text size="sm" c="dimmed" mt="xs">
                      This controls how long a push message is retained by the push service. Once the MET period elapses
                      and the message still has not been consumed, the message will expire. The messaging consumers will
                      never see an expired message.
                    </Text>
                  </Grid.Col>
                </Grid>
                <Grid mb="lg">
                  <Grid.Col span={{ base: 12, sm: 2 }}>
                    <Text fw={500}>Notification options</Text>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 2 }}>
                    <div className={styles.checkboxWithTooltip}>
                      <Checkbox
                        name="requireInteraction"
                        label="Require Interaction"
                        onChange={(e) => handleChange('requireInteraction', e.target.checked)}
                        checked={pushNotification.requireInteraction}
                      />
                      <Tooltip
                        label="RequireInteraction option forces a notification to stay visible until the user interacts with it. This will show the notification until the user dismisses or clicks your notification."
                        position="top"
                        multiline
                        w={300}
                      >
                        <IconQuestionMark size={14} style={{ cursor: 'help' }} />
                      </Tooltip>
                    </div>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 2 }}>
                    <div className={styles.checkboxWithTooltip}>
                      <Checkbox
                        name="renotify"
                        label="Renotify"
                        onChange={(e) => handleChange('renotify', e.target.checked)}
                        checked={pushNotification.renotify}
                      />
                      <Tooltip
                        label="This largely applies to mobile devices. Setting this option makes new notifications vibrate and play a system sound."
                        position="top"
                        multiline
                        w={300}
                      >
                        <IconQuestionMark size={14} style={{ cursor: 'help' }} />
                      </Tooltip>
                    </div>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 2 }}>
                    <div className={styles.checkboxWithTooltip}>
                      <Checkbox
                        name="silent"
                        label="Silent"
                        onChange={(e) => handleChange('silent', e.target.checked)}
                        checked={pushNotification.silent}
                      />
                      <Tooltip
                        label="This option allows you to show a new notification but prevents the default behavior of vibration, sound and turning on the device display."
                        position="top"
                        multiline
                        w={300}
                      >
                        <IconQuestionMark size={14} style={{ cursor: 'help' }} />
                      </Tooltip>
                    </div>
                  </Grid.Col>
                </Grid>
                <Box pt="lg">
                  <DailyLimitSettings
                    model={pushNotification}
                    onChange={(key, value) => handleChange(key, value)}
                    channelLabel="push notifications"
                    maxPerDay={domain.maxPushNotificationsPerDay}
                    accountId={accountId}
                    domainId={domainId}
                    settingsTab="pushNotification"
                    disableReschedule={pushNotification.platforms.includes('appInbox')}
                  />
                </Box>
              </Card.Section>
            </Card>
          )}

          {pushNotification.platforms.includes('web') && (
            <Card withBorder mb="md">
              <Card.Section inheritPadding py="md">
                <Text fw={700} size="lg" mb="md">
                  <IconBrandChrome size={14} /> Web Targeting
                </Text>
                <Grid mb="md">
                  <Grid.Col span={{ base: 12, sm: 2 }}>
                    <Text fw={500}>
                      Link{' '}
                      <Text span c="red">
                        *
                      </Text>
                    </Text>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 10 }}>
                    <TextInput
                      name="url"
                      value={pushNotification.url || ''}
                      placeholder={`e.g. ${domain.url}`}
                      onChange={(e) => handleChange('url', e.target.value)}
                      error={webUrlError}
                      maxLength={MAX_URL_LENGTH}
                    />
                    <Text size="sm" c="dimmed" mt="xs">
                      This is the URL where a click on your push notification will lead.
                    </Text>
                  </Grid.Col>
                </Grid>
              </Card.Section>
            </Card>
          )}

          {pushNotification.platforms.some((p) => ['android', 'ios'].includes(p)) && (
            <Card withBorder mb="md">
              <Card.Section inheritPadding py="md">
                <Text fw={700} size="lg" mb="md">
                  <IconDeviceMobile size={16} /> Mobile Targeting
                </Text>
                <Grid mb="md">
                  <Grid.Col span={{ base: 12, sm: 2 }}>
                    <Text fw={500}>
                      Target{' '}
                      <Text span c="red">
                        *
                      </Text>
                    </Text>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 8 }}>
                    <SegmentedControl
                      value={pushNotification.mobileTarget ?? ''}
                      onChange={(mobileTarget) => {
                        setPushNotification({ ...pushNotification, mobileTarget: mobileTarget || null });
                      }}
                      data={[
                        // SegmentedControl only carries string values, so "main screen" round-trips as ''.
                        { label: 'Main Screen', value: '' },
                        ...(pushNotification.platforms.includes('appInbox')
                          ? [{ label: 'Open Inbox', value: 'inbox' }]
                          : []),
                        { label: 'Specific Screen', value: 'screen' },
                        { label: 'URL', value: 'url' },
                      ]}
                    />
                  </Grid.Col>
                </Grid>
                {pushNotification.mobileTarget === 'url' && (
                  <Grid mb="md">
                    <Grid.Col span={{ base: 12, sm: 2 }}>
                      <Text fw={500}>
                        URL
                        <Text span c="red">
                          *
                        </Text>
                      </Text>
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 10 }}>
                      <TextInput
                        name="mobileTargetUrl"
                        value={pushNotification.mobileTargetUrl || ''}
                        placeholder={`e.g. ${domain.url} or myapp://consumer.app/home`}
                        onChange={(e) => handleChange('mobileTargetUrl', e.target.value)}
                        error={mobileUrlError}
                        maxLength={MAX_URL_LENGTH}
                      />
                      <Text size="sm" c="dimmed" mt="xs">
                        This is the URL where a click on your push notification will lead.
                      </Text>
                    </Grid.Col>
                  </Grid>
                )}

                {pushNotification.mobileTarget === 'screen' && (
                  <>
                    <Grid mb="md">
                      <Grid.Col span={{ base: 12, sm: 2 }}>
                        <Text fw={500}>
                          Screen
                          <Text span c="red">
                            *
                          </Text>
                        </Text>
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, sm: 10 }}>
                        <TextInput
                          name="mobileTargetScreen"
                          value={pushNotification.mobileTargetScreen || ''}
                          placeholder={`e.g. Android: com.app.activities.MainActivity or iPhone: /home`}
                          onChange={(e) => handleChange('mobileTargetScreen', e.target.value)}
                        />
                        <Text size="sm" c="dimmed" mt="xs">
                          This is the name of the screen that will open
                        </Text>
                      </Grid.Col>
                    </Grid>
                    <Grid mb="md">
                      <Grid.Col span={{ base: 12, sm: 2 }}>
                        <Text fw={500}>KV Pairs</Text>
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, sm: 10 }}>
                        <Mapper
                          rows={pushNotification.mobileTargetKV}
                          setRows={(mobileTargetKV) => setPushNotification({ ...pushNotification, mobileTargetKV })}
                        />
                        <Text size="sm" c="dimmed" mt="xs">
                          These are key-value pairs which will be sent as parameters to the selected app screen
                        </Text>
                      </Grid.Col>
                    </Grid>
                  </>
                )}
              </Card.Section>
            </Card>
          )}

          {pushNotification.platforms.includes('appInbox') && (
            <Card withBorder mb="md">
              <Card.Section inheritPadding py="md">
                <Text fw={700} size="lg" mb="md">
                  <IconInbox size={16} /> App Inbox
                </Text>
                <Grid mb="md">
                  <Grid.Col span={{ base: 12, sm: 2 }}>
                    <Text fw={500}>Expiry (days)</Text>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 10 }}>
                    <NumberInput
                      min={1}
                      max={365}
                      value={appInbox?.ttlSeconds ? Math.round(appInbox.ttlSeconds / 86400) : ''}
                      placeholder="e.g. 30"
                      onChange={(value) => setAppInbox({ ...appInbox, ttlSeconds: value ? value * 86400 : null })}
                    />
                    <Text size="sm" c="dimmed" mt="xs">
                      How many days to keep the message in the inbox. Leave empty for no expiry.
                    </Text>
                  </Grid.Col>
                </Grid>
              </Card.Section>
            </Card>
          )}

          <Group justify="flex-end" gap="xs" pt="md">
            {formActionButtons}
          </Group>
        </Tabs.Panel>
      </Tabs>
      <ErrorMessage message={error?.message} />
      <ConfirmationModal
        show={deleteError ? false : confirmDeleteOpen}
        close={() => setConfirmDeleteOpen(false)}
        confirm={(cb) => deletePushNotification(cb)}
        message={<WarningMessageOnDelete type="Push Notification" />}
      />
      {deleteError ? (
        <Box ta="right">
          <ErrorMessage message={deleteError} />
        </Box>
      ) : null}
    </Grid.Col>
  );
};

export default PushNotificationForm;
